import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChicagoStatus } from '@core/util/chicago';
import { MatterPropertyType } from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import {
	FCTTitleInsuranceState,
	TitlePlusStatus,
} from '@models/title-insurance';
import { StewartStatus } from '@models/title-insurance/stewart';
import {
	TitleInsuranceCompanyName,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { TitleInsuranceContactInfoModalComponent } from '../contact-info-modal/contact-info-modal.component';
import { FinanceSettingsComponent } from '../finance-settings/finance-settings.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { TestHooks } from './title-insurance-toolbar.hooks';

@Component({
	selector: 'app-title-insurance-toolbar',
	templateUrl: './title-insurance-toolbar.component.html',
	styleUrls: ['./title-insurance-toolbar.component.scss'],
	standalone: false,
})
export class TitleInsuranceToolbarComponent implements OnChanges {
	testHooks = TestHooks;
	public allowCancel: boolean;
	public backUrlParams = { 'back-string': 'Title insurance' };
	public completedOrder: boolean;
	public ICON = ICON;
	public propertyType: string;
	public propertyUrl: string;
	public quoteLabel = 'Get quote';
	public quoteLink: string;
	public showCancel: boolean;
	public submitIcon = ICON.RESEND;
	public submitOrderText = 'Start order';
	public cancelLabel = 'Cancel order';
	public titleInsuranceProvider = TitleInsuranceProvider;
	public usageType = PropertyUsage;

	@Input() provider: TitleInsuranceProvider;
	@Input() orderValid: boolean;
	@Input() status:
		| ChicagoStatus
		| StewartStatus
		| FCTTitleInsuranceState
		| TitlePlusStatus;
	@Input() hasQuote: boolean;
	@Input() company: string;
	@Input() propertyUsage: PropertyUsage = PropertyUsage.RESIDENTIAL;
	@Input() set orderSubmitted(_orderSubmitted: boolean) {
		this.completedOrder = _orderSubmitted;
	}

	@Output() openModal: EventEmitter<void> = new EventEmitter();
	@Output() cancelOrder: EventEmitter<void> = new EventEmitter();

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.propertyTypeString)
	propertyTypeString$: Observable<MatterPropertyType>;

	constructor(
		private dialog: MatDialog,
		private snackbarService: SnackbarService,
		private analytics: AnalyticsService,
	) {}

	ngOnChanges(): void {
		this.propertyUrl = UrlService.Matter_Property_Details(this.matterId);

		switch (this.provider) {
			case TitleInsuranceProvider.CHICAGO:
				this.setChicagoVariables(this.status as ChicagoStatus);
				break;
			case TitleInsuranceProvider.STEWART:
				this.setStewartVariables(this.status as StewartStatus);
				break;
			case TitleInsuranceProvider.FCT:
				this.setFCTVariables(this.status as FCTTitleInsuranceState);
				break;
			case TitleInsuranceProvider.MANUAL:
				this.setManualVariable();
				break;
			case TitleInsuranceProvider.TITLE_PLUS:
				this.setTitlePLUSVariable(this.status as TitlePlusStatus);
				break;
		}

		if (this.hasQuote) this.quoteLabel = 'Get another quote';
	}

	toggleShowContactInfo(): void {
		let { provider } = this;
		if (provider === TitleInsuranceProvider.MANUAL) {
			switch (this.company) {
				case TitleInsuranceCompanyName[TitleInsuranceProvider.CHICAGO]:
					provider = TitleInsuranceProvider.CHICAGO;
					break;
				case TitleInsuranceCompanyName[TitleInsuranceProvider.STEWART]:
					provider = TitleInsuranceProvider.STEWART;
					break;
				case TitleInsuranceCompanyName[TitleInsuranceProvider.FCT]:
					provider = TitleInsuranceProvider.FCT;
					break;
				case TitleInsuranceCompanyName[TitleInsuranceProvider.TITLE_PLUS]:
					provider = TitleInsuranceProvider.TITLE_PLUS;
					break;
			}
		}

		this.analytics.logEvent(AnalyticsEvent.TITLE_INSURANCE_VIEW_CONTACT_INFO, {
			provider,
		});

		const contactModal = this.dialog.open(
			TitleInsuranceContactInfoModalComponent,
			{
				data: {
					provider,
				},
				maxWidth: '500px',
				width: '90%',
			},
		);
		contactModal.afterClosed().subscribe((result) => {
			if (result) {
				this.snackbarService.openSnackBar(
					{ text: 'Main contact details saved!', type: 'success' },
					{
						duration: 3000,
						panelClass: 'ml-96',
					},
				);
			}
		});
	}

	openPortal(): void {
		this.openModal.emit();
	}

	cancel(): void {
		this.cancelOrder.emit();
	}

	openFinancialSettings(): void {
		this.dialog.open(FinanceSettingsComponent);
	}

	setChicagoVariables = (status: ChicagoStatus): void => {
		this.showCancel = true;
		this.allowCancel = true;
		this.quoteLink = 'https://express.ctic.ca/secure/process.aspx?uc=ucQuote';
		switch (status) {
			case ChicagoStatus.NOT_SUBMITTED:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
			case ChicagoStatus.UNDER_REVIEW:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				break;
			case ChicagoStatus.WAITING_FOR_INFORMATION:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				break;
			case ChicagoStatus.READY_FOR_PICKUP:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				this.completedOrder = true;
				break;
			case ChicagoStatus.CANCELLED:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				this.showCancel = false;
				break;
			default:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
		}
	};

	setStewartVariables(status: StewartStatus): void {
		this.showCancel = true;
		this.allowCancel = true;
		this.quoteLink = 'http://www.stewart.ca/quote';
		switch (status) {
			case StewartStatus.NOT_SUBMITTED:
				this.submitOrderText = 'Start order';
				this.submitIcon = ICON.RESEND;
				break;
			case StewartStatus.ORDER_RESUBMIT_REQUIRED:
				this.submitOrderText = 'Resubmit order';
				this.submitIcon = ICON.RESEND;
				break;
			case StewartStatus.FLAGGED:
				this.submitOrderText = 'Amend order';
				this.submitIcon = ICON.EDIT;
				break;
			case StewartStatus.CANCELLED:
				this.showCancel = false;
				this.submitOrderText = 'Reactivate order';
				this.submitIcon = ICON.RESEND;
				break;
			case StewartStatus.ORDERED:
				this.submitOrderText = 'Amend order';
				this.submitIcon = ICON.EDIT;
				this.completedOrder = true;
				break;
			case StewartStatus.DECLINED:
				this.showCancel = false;
				this.orderValid = false;
				break;
			default:
				this.submitOrderText = 'Start order';
				this.submitIcon = ICON.RESEND;
				break;
		}
	}

	setFCTVariables(status: FCTTitleInsuranceState): void {
		this.showCancel = true;
		if (this.propertyUsage === PropertyUsage.COMMERCIAL) {
			this.cancelLabel = 'Clear order';
			if (this.orderSubmitted) {
				this.completedOrder = true;
			}

			return;
		}
		switch (status) {
			case FCTTitleInsuranceState.DRAFT:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
			case FCTTitleInsuranceState.COMPLETED:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				this.completedOrder = true;
				break;
			case FCTTitleInsuranceState.ON_HOLD:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				break;
			case FCTTitleInsuranceState.IN_UNDERWRITING:
			case FCTTitleInsuranceState.PENDING_PROCESSING:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Amend order';
				break;
			case FCTTitleInsuranceState.PENDING_CANCELLATION:
			case FCTTitleInsuranceState.CANCELLED:
				this.showCancel = false;
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Amend order';
				break;
			case FCTTitleInsuranceState.PENDING_UPDATE:
			case FCTTitleInsuranceState.WAITING_FOR_DOCUMENTS:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Update order';
				break;
			default:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
		}
	}

	setManualVariable(): void {
		this.showCancel = true;
		this.completedOrder = true;
		this.cancelLabel = 'Clear order';
	}

	setTitlePLUSVariable = (status: TitlePlusStatus): void => {
		this.showCancel = true;
		this.allowCancel = true;
		this.quoteLink = 'https://titleplus.ca/premium-calculator/';
		switch (status) {
			case TitlePlusStatus.NOT_SUBMITTED:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
			case TitlePlusStatus.UNDER_REVIEW:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				break;
			case TitlePlusStatus.USER_ACTION_REQUIRED:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				break;
			case TitlePlusStatus.ISSUED:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				this.completedOrder = true;
				break;
			case TitlePlusStatus.CANCELLED:
				this.submitIcon = ICON.EDIT;
				this.submitOrderText = 'Amend order';
				this.showCancel = false;
				break;
			case TitlePlusStatus.DECLINED:
			case TitlePlusStatus.DELETED:
				this.showCancel = false;
				this.orderValid = false;
				break;
			default:
				this.submitIcon = ICON.RESEND;
				this.submitOrderText = 'Start order';
				break;
		}
	};
}
